import React, { useCallback, useEffect } from 'react';
import { noop } from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '../../../core/components/modal/Modal';
import {
  BulkEditOwnerRequest, EditOwnerSchema, PartRequestBulkItemDto,
} from '../types';
import { createOwnerRequest } from './utils';
import ButtonActions from '../../../core/components/button-actions/ButtonActions';
import { DialogSection, SelectionType } from '../SelectionForm';
import { useEditOwner } from './useEditOwner';
import { OwnerSelectField } from './OwnerSelectField';

interface EditLocationDialogProps {
  onClose?: (nextItem: null) => void;
  row: PartRequestBulkItemDto | null;
}

export const EditOwnerDialog = ({ onClose = noop, row }: EditLocationDialogProps) => {
  const form = useForm<BulkEditOwnerRequest>({
    resolver: zodResolver(EditOwnerSchema),
  });

  const { reset, handleSubmit } = form;

  useEffect(() => {
    if (row) {
      reset(createOwnerRequest(row));
    }
  }, [row, reset]);

  const handleClose = useCallback(() => {
    onClose(null);
  }, [onClose]);

  const [pending, updateLocation] = useEditOwner(handleClose);

  const handleSave = useCallback(() => {
    handleSubmit(updateLocation)();
  }, [handleSubmit, updateLocation]);

  return (
    <FormProvider {...form}>
      <Modal visible={!!row} title="Edit Owner" onCancel={handleClose} destroyOnClose>
        <DialogSection>
          <OwnerSelectField />
          <SelectionType lineId={row?.lineId} />
        </DialogSection>
        <ButtonActions
          cancelLabel="Cancel"
          createLabel="Save"
          createType="button"
          cancelClick={handleClose}
          createClick={handleSave}
          offsetTop
          isLoading={pending}
        />
      </Modal>
    </FormProvider>
  );
};
